<!--
    Implementation of multiple AIS components,
    with custom templates.

    The docs for each component are really useful.
    e.g. https://www.algolia.com/doc/api-reference/widgets/configure/vue/

    (make sure you search the VUE specific docs!)
-->
<ais-configure :hitsPerPage="hitsPerPage" filters="objectSubsite:0" />
<div class="flex items-center mb-2">
    <ais-search-box class="basis-full relative">
        <template v-slot="{
                    currentRefinement,
                    refine
                }">
            <div class="absolute inset-y-0 left-0 flex items-center pl-0.75 pointer-events-none">
                <svg class="w-1.75 h-1.75 text-primary" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                    fill="none" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke="currentColor" stroke-linejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
            </div>
            <input type="search" placeholder="Enter your search text here" :value="currentRefinement"
                @keyup.enter="refine($event.currentTarget.value)" class="
                        basis-full
                        border-none
                        shadow-xl
                        !pl-3
                        !pr-[6.5rem]
                        !py-1
                    ">
            <div class="
                    absolute
                    inset-y-0
                    right-[0.5rem]
                    flex
                    items-center
                    pl-1
                ">
                <button @click="
                            refine($event.currentTarget.parentNode.previousSibling.value)
                        " class="button my-0 !py-[0.5rem]">
                    Search
                    <span :hidden="!currentRefinement">
                        again
                    </span>
                </button>
            </div>
        </template>
    </ais-search-box>
</div>

<ais-stats>
    <template v-slot="{ hitsPerPage, nbPages, nbHits, page, processingTimeMS, query }">
        <p v-if="query" class="text-center">
            You searched for the keyword <strong><q>{{ query }}</q></strong>.
        </p>
    </template>
</ais-stats>

<div class="flex flex-wrap md:flex-nowrap gap-3 mt-4">

    <ais-menu attribute="objectSearchFilter" :transform-items="alphabetiseFacets" class="
            basis-full
            md:basis-1/4
        ">
        <template v-slot="{
                items,
                refine,
                createURL,
                sendEvent,
            }">
            <h3 class="tagline uppercase block text-primary text-center md:text-left border-b border-b-gray-200 pb-1">
                Filter by Category
            </h3>
            <ul v-if="items.length" class="grid grid-cols-2 gap-1 md:grid-cols-1">
                <li class="hover:text-primary">
                    <ais-clear-refinements>
                        <template v-slot="{ canRefine, refine }">
                            <button 
                                @click.prevent="refine" 
                                :class="canRefine ? '' : 'text-primary font-bold'"
                            >
                                Show all
                            </button>
                        </template>
                    </ais-clear-refinements>
                </li>
                <li class="hover:text-primary" v-for="item in items" :key="item.value">
                    <button 
                        @click.prevent="refine(item.value)"
                        :class="item.isRefined ? 'text-primary font-bold' : 'hover:text-primary'"
                    >
                        {{ item.label }} <small>({{ item.count }})</small>
                    </button>
                </li>
            </ul>
        </template>
    </ais-menu>

    <ais-hits class="
                basis-full
                md:basis-3/4
            ">
        <template v-slot:item="{ item }">
            <a :href="item.objectLink" class="flex flex-wrap border-b border-b-gray-200 mb-1 pb-1 lg:mb-1.5 lg:pb-1.5">
                <picture class="flex-none w-[70px] md:w-[100px]">
                    <img v-if="item.objectSmallImage" :src="item.objectSmallImage" alt="" width="100" height="100"
                        loading="lazy" />
                    <img v-else src="../assets/no-image-1x1.png" alt="" width="100" height="100" loading="lazy" />
                </picture>
                <div class="flex-1 ms-1">
                    <small class="text-xs tagline">{{ item.objectSearchFilter }}</small>
                    <h2 class="h5 mb-0.5 hover:text-primary">{{ item.objectTitle }}</h2>
                    <p v-html="item.objectSummary" class="my-0">
                    </p>
                </div>
            </a>
        </template>
    </ais-hits>

</div>

<ais-pagination :padding="1" @page-change="scrollToTop">
    <template v-slot="{
            currentRefinement,
            nbPages,
            pages,
            isFirstPage,
            isLastPage,
            refine,
            createURL
        }">
        <ul class="pagination list-style-none flex justify-center mb-2">
            <li v-if="!isFirstPage">
                <a :href="createURL(currentRefinement - 1)" @click.prevent="refine(currentRefinement - 1)"
                    class="mx-qtr button hollow p-[12px]">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-1.5 h-1.5 stroke-2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                    <span class="sr-only">Previous</span>
                </a>
            </li>
            <li v-for="page in pages" :key="page" class="hidden md:block">
                <a :href="createURL(page)" @click.prevent="refine(page)" class="mx-qtr py-0.75 px-[1.2rem] button"
                    :class="{ hollow: page !== currentRefinement }">
                    {{ page + 1 }}
                </a>
            </li>
            <li v-if="!isLastPage">
                <a :href="createURL(currentRefinement + 1)" @click.prevent="refine(currentRefinement + 1)"
                    class="mx-qtr button hollow p-[12px]">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-1.5 h-1.5 stroke-2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                    <span class="sr-only">Next</span>
                </a>
            </li>
        </ul>
    </template>
</ais-pagination>
